import React from "react";
import { Drawer, Skeleton, List, Input, Tag, Space } from "antd";
import { AppDto } from "../../types/devicemanagementtypes";

const { Search } = Input;

interface DeviceAppsDrawerProps {
    visible: boolean;
    onClose: () => void;
    deviceName?: string;
    apps: AppDto[];
    loading: boolean;
    pagination: {
        current: number;
        pageSize: number;
        total: number;
    };
    onPageChange: (page: number, pageSize: number) => void;
    onSearch: (search: string) => void;
    searchValue: string;
}

const DeviceAppsDrawer: React.FC<DeviceAppsDrawerProps> = ({
    visible,
    onClose,
    deviceName,
    apps,
    loading,
    pagination,
    onPageChange,
    onSearch,
    searchValue
}) => {
    return (
        <Drawer
            title={`Installed Apps on ${deviceName ?? ""}`}
            placement="right"
            width={800}
            onClose={onClose}
            open={visible}
            styles={{
                header: {
                    color: "white",
                    backgroundColor: "var(--color-primary)",
                    fontWeight: 350,
                    fontSize: "14px",
                    border: 0,
                    minHeight: "40px",
                },
            }}
        >
            <Search
                placeholder="Search apps..."
                allowClear
                style={{ marginBottom: 16 }}
                value={searchValue}
                onChange={(e) => onSearch(e.target.value)}
                onSearch={onSearch}
            />

            {loading ? (
                <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
                <List
                    itemLayout="vertical"
                    dataSource={apps}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: ["5", "10", "20", "50"],
                        onChange: onPageChange,
                        showTotal: (total, range) =>
                            `Showing ${range[0]}–${range[1]} of ${total} apps`,
                    }}
                    renderItem={(app: AppDto) => (
                        <List.Item key={app.appId}>
                            <List.Item.Meta
                                title={
                                    <Space>
                                        <strong>{app.name}</strong>
                                        <Tag color="blue">v{app.versionName}</Tag>
                                        {app.appType && (
                                            <Tag color="purple">
                                                {app.appType.toUpperCase()}
                                            </Tag>
                                        )}
                                    </Space>
                                }
                                description={
                                    <div>
                                        <div>
                                            <strong>Package:</strong> {app.packageName}
                                        </div>
                                        {app.state && (
                                            <div>
                                                <strong>State:</strong>{" "}
                                                <Tag
                                                    color={
                                                        app.state === "INSTALLED"
                                                            ? "green"
                                                            : app.state === "REMOVED"
                                                                ? "red"
                                                                : "orange"
                                                    }
                                                >
                                                    {app.state}
                                                </Tag>
                                            </div>
                                        )}
                                        <div style={{ marginTop: 4 }}>
                                            {app.whitelisted ? (
                                                <Tag color="green">Whitelisted</Tag>
                                            ) : (
                                                <Tag color="red">Not Whitelisted</Tag>
                                            )}
                                            {app.isActive && <Tag color="blue">Active</Tag>}
                                            {app.isUninstallable ? (
                                                <Tag color="volcano">Uninstallable</Tag>
                                            ) : (
                                                <Tag color="default">System App</Tag>
                                            )}
                                            {app.isDataClearable && (
                                                <Tag color="gold">Data Clearable</Tag>
                                            )}
                                        </div>
                                    </div>
                                }
                            />
                        </List.Item>
                    )}
                />
            )}
        </Drawer>
    );
};

export default DeviceAppsDrawer;